import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, Activity, Aperture, ChevronRight } from 'lucide-react';

const COLORS = {
  primary: '#0760ad',
  secondary: '#098a48',
  tertiary: '#118a94',
  green: '#339166',
  white: '#FFFFFF',
  lightGray: '#f8fafc',
  darkText: '#1a365d'
};

const DEFAULT_SERVICES = {
  heading: "Advanced Healthcare Solutions",
  subheading: "Combining cutting-edge technology with compassionate care for better health outcomes",
  buttonText: "Explore All Services",
  services: [
    {
      title: "Digital Health Portal",
      highlight: "Online Reports",
      description: "Access your medical reports anytime through our secure patient portal.",
      features: ["24/7 access to test results", "Secure document sharing"],
      cta: "Login to Portal",
      icon: "FileText",
      image: "",
      color: COLORS.primary,
      bgClass: "bg-blue-50"
    }
  ]
};

const getIconComponent = (iconName) => {
  switch(iconName) {
    case 'FileText': return <FileText className="w-6 h-6" />;
    case 'Activity': return <Activity className="w-6 h-6" />;
    case 'Aperture': return <Aperture className="w-6 h-6" />;
    default: return <FileText className="w-6 h-6" />;
  }
};

const ServiceCard = ({ service }) => {
  return (
    <motion.div 
      whileHover={{ y: -5 }}
      className={`relative rounded-2xl overflow-hidden shadow-lg group ${service.bgClass}`}
    >
      <div className="absolute inset-0 mix-blend-overlay opacity-20 text-justify">
        {service.image && (
          <img 
            src={service.image} 
            alt="" 
            className="w-full h-full object-cover"
          />
        )}
      </div>

      <div className="relative z-10 p-8 h-full flex flex-col">
        <div className="flex items-start gap-4 mb-6">
          <div 
            className="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: service.color, color: COLORS.white }}
          >
            {getIconComponent(service.icon)}
          </div>
          <div>
            <h3 className="text-xl font-bold" style={{ color: COLORS.darkText }}>
              {service.title}
            </h3>
            <span 
              className="text-sm font-semibold"
              style={{ color: service.color }}
            >
              {service.highlight}
            </span>
          </div>
        </div>

        <p className="mb-6 text-justify" style={{ color: COLORS.darkText }}>
          {service.description}
        </p>

        <ul className="mb-8 space-y-3">
          {service.features.map((feature, i) => (
            <li key={i} className="flex items-start">
              <div 
                className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0"
                style={{ backgroundColor: service.color }}
              ></div>
              <span style={{ color: COLORS.darkText }}>{feature}</span>
            </li>
          ))}
        </ul>

        <motion.button
          whileHover={{ x: 5 }}
          className="mt-auto flex items-center gap-2 font-medium self-start"
          style={{ color: service.color }}
        >
          {service.cta}
          <ChevronRight className="w-5 h-5" />
        </motion.button>
      </div>
    </motion.div>
  );
};

const HealthcareServicesGrid = ({ isAdmin = false, onEdit }) => {
  const [servicesData, setServicesData] = useState(DEFAULT_SERVICES);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchServicesData = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/home/healthcare-services-grid/`);
        if (response.ok) {
          const data = await response.json();
          setServicesData(data);
        } else {
          console.error('Failed to fetch services data');
        }
      } catch (error) {
        console.error('Error fetching services data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchServicesData();
  }, []);

  if (isLoading) {
    return (
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex justify-center">
            <span className="loading loading-spinner loading-lg"></span>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <span 
            className="inline-block px-4 py-2 rounded-full text-sm font-medium mb-4"
            style={{ 
              backgroundColor: `${COLORS.primary}10`,
              color: COLORS.primary
            }}
          >
            Our Services
          </span>
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: COLORS.darkText }}>
            {servicesData.heading || "Advanced Healthcare Solutions"}
          </h2>
          <div className="w-16 h-1 mx-auto mb-6 rounded-full" style={{ 
            background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.tertiary})` 
          }}></div>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: COLORS.darkText }}>
            {servicesData.subheading || "Combining cutting-edge technology with compassionate care"}
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {servicesData.services?.map((service, index) => (
            <ServiceCard key={index} service={service} />
          ))}
        </div>

        <div className="text-center mt-12">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="px-8 py-3 rounded-lg font-medium text-white shadow-lg"
            style={{ 
              background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.tertiary})`,
              boxShadow: `0 4px 20px ${COLORS.primary}30`
            }}
          >
            <a href ={servicesData.buttonLink}>
            {servicesData.buttonText || "Explore All Services"}
            </a>
          </motion.button>
       
        </div>
      </div>
    </section>
  );
};

export default HealthcareServicesGrid;